import { Box, Chip, Divider, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { logger } from '../../services/logger/logger';
import { ColDef } from 'ag-grid-community';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import Chips from '../../components/common/Chips';
import ClientSideAgGrid from '../../components/common/customAgGrid/ClientSideAgGrid';
import CustomActionButton from '../../components/common/customAgGrid/CustomActionButton';
import CustomAgMultiSelectFilter from '../../components/common/customAgGrid/CustomAgMultiSelectFilter';
import CustomAgTextFilter from '../../components/common/customAgGrid/CustomAgTextFilter';
import DeleteConfirmModal from '../../components/common/DeleteConfirmModal';
import SideDrawer from '../../components/common/SideDrawer';
import EditCompaniesSuppliersModal from '../../components/companies/EditCompaniesSuppliersModal';
import PageHeader from '../../components/PageHeader';
import {
  useGetCompaniesMakersDataQuery,
  useGetCompaniesMakersListQuery,
} from '../../store/apiSlices/companies/companiesMakers/companiesMakersApis';
import {
  useCreateCompaniesSuppliersMutation,
  useDeleteCompaniesSuppliersMutation,
  useGetCompaniesSuppliersDataQuery,
  useGetCompaniesSuppliersListQuery,
  useGetManagersShortListQuery,
  useUpdateCompaniesSuppliersMutation,
} from '../../store/apiSlices/companies/companiesSuppliers/companiesSuppliersApis';
import { canEditReferenceData } from '../../utils/permissions';
import { showSuccessToast } from '../../components/common/BaseToaster';
import { TableCardContent } from '../../components/common/styled/TableCardContent';
import { useDeleteConfirmation } from '../../hooks/useDeleteConfirmation';
import { useTranslation } from 'react-i18next';

// ------------------- Types -------------------
interface MakerItem {
  makerId: number;
  makerName: string;
}

interface ManagerItem {
  managerId: number;
  managerName: string;
}

interface CompaniesSuppliers {
  srno: number;
  shortName: string;
  supplier: string;
  makers: MakerItem[];
  managers: ManagerItem[];
  makersIds: number[];
  managerIds: number[];
  status: string;
  actions: string;
}

interface ActionButton {
  label: string;
  variant?: 'blue' | 'outlinedBlue' | 'red' | 'gray' | 'text';
  onClick: () => void;
}

interface BreadcrumbOption {
  label: string;
  isActive: boolean;
  onClick: () => void;
}

// ------------------- Component -------------------
type SupplierFormValues = {
  shortName: string;
  companyId: string;
  makers: number[];
  managers: number[];
  status: string;
};

const CompaniesSuppliersContainer: React.FC = () => {
  const { t } = useTranslation();
  const userInfo = useSelector((state: any) => state.user.userInfo);
  const canAddorEdit = canEditReferenceData(userInfo?.authorityName);
  const theme = useTheme();
  const [showAddCompaniesSuppliersModal, setShowAddCompaniesSuppliersModal] =
    useState<boolean>(false);
  const [editingSupplier, setEditingSupplier] = useState<CompaniesSuppliers | null>(null);
  const [, setDidApplyPrefill] = useState<boolean>(false);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<SupplierFormValues>({
    defaultValues: {
      shortName: '',
      companyId: '',
      makers: [],
      managers: [],
      status: 'Active',
    },
  });

  const [selectedSupplierName, setSelectedSupplierName] = useState<string>('');
  const [isMakersDrawerOpen, setIsMakersDrawerOpen] = useState<boolean>(false);
  // Store makers data from row (no separate API call needed)
  const [selectedMakers, setSelectedMakers] = useState<MakerItem[]>([]);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [resetFiltersSignal, setResetFiltersSignal] = useState(false);
  const [tableColumns, setTableColumns] = useState<ColDef[]>([]);

  const openMakersDrawer = useCallback((makers: MakerItem[], supplierName?: string) => {
    setSelectedMakers(makers);
    if (supplierName) setSelectedSupplierName(supplierName);
    setIsMakersDrawerOpen(true);
  }, []);

  const closeMakersDrawer = useCallback(() => {
    setIsMakersDrawerOpen(false);
    setSelectedMakers([]);
    setSelectedSupplierName('');
  }, []);

  // Managers drawer state
  const [selectedManagerSupplierName, setSelectedManagerSupplierName] = useState<string>('');
  const [isManagersDrawerOpen, setIsManagersDrawerOpen] = useState<boolean>(false);
  // Store managers data from row (no separate API call needed)
  const [selectedManagers, setSelectedManagers] = useState<ManagerItem[]>([]);

  const openManagersDrawer = useCallback((managers: ManagerItem[], supplierName?: string) => {
    setSelectedManagers(managers);
    if (supplierName) setSelectedManagerSupplierName(supplierName);
    setIsManagersDrawerOpen(true);
  }, []);

  const closeManagersDrawer = useCallback(() => {
    setIsManagersDrawerOpen(false);
    setSelectedManagers([]);
    setSelectedManagerSupplierName('');
  }, []);

  // API calls
  const {
    data: companiesSuppliersList,
    isLoading,
    refetch,
  } = useGetCompaniesSuppliersListQuery(undefined, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  // Get companies suppliers data for the modal - only fetch when modal is open
  const { data: companiesSuppliersData } = useGetCompaniesSuppliersDataQuery(undefined, {
    skip: !showAddCompaniesSuppliersModal,
    refetchOnMountOrArgChange: true,
  });
  const { data: makersList } = useGetCompaniesMakersListQuery({});
  const { data: managersShortList } = useGetManagersShortListQuery();
  const [createSupplier] = useCreateCompaniesSuppliersMutation();
  const { data: companiesMakersData } = useGetCompaniesMakersDataQuery();
  const [updateSupplier] = useUpdateCompaniesSuppliersMutation();
  const [deleteSupplier, { isLoading: isDeleting }] = useDeleteCompaniesSuppliersMutation();

  const { showDeleteConfirm, handleConfirmDelete, onDeleteClick, onCloseDeleteConfirm } =
    useDeleteConfirmation<CompaniesSuppliers>({
      deleteMutation: deleteSupplier,
      refetch,
      itemName: 'supplier',
      idField: 'srno',
    });

  // Ensure API refetches when needed
  useEffect(() => {
    refetch();
  }, [refetch]);

  // Page header action buttons
  const actionBtns: ActionButton[] = canAddorEdit
    ? [
        {
          label: t('companies.addSupplier'),
          variant: 'blue',
          onClick: () => {
            reset({
              shortName: '',
              companyId: '',
              makers: [],
              managers: [],
              status: 'Active',
            });
            setDidApplyPrefill(false);
            setShowAddCompaniesSuppliersModal(true);
          },
        },
      ]
    : [];

  // Handle edit button click
  const handleEditClick = useCallback(
    (rowData: CompaniesSuppliers) => {
      setEditingSupplier(rowData);
      setDidApplyPrefill(true); // No need for prefill anymore since data is in the row

      // Reset form with initial data from rowData - makers/managers are now available directly
      const initialMakers = rowData.makersIds || [];
      const initialManagers = rowData.managerIds || [];

      reset({
        shortName: rowData.shortName,
        companyId: String(rowData.supplier ?? ''),
        makers: initialMakers,
        managers: initialManagers,
        status: rowData.status,
      });

      setShowAddCompaniesSuppliersModal(true);
    },
    [reset],
  );

  // Table columns
  const columns: ColDef[] = useMemo(
    () => [
      {
        field: 'id',
        headerName: t('table.columns.id'),
        sortable: true,
        width: 120,
        maxWidth: 120,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgTextFilter,
      },
      {
        field: 'shortName',
        headerName: t('table.columns.shortName'),
        width: 200,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
      {
        field: 'supplier',
        headerName: t('table.columns.company'),
        width: 250,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
      {
        field: 'makers',
        headerName: t('table.columns.makers'),
        width: 250,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
        // Return comma-separated maker names for filter
        valueGetter: (params: any) => {
          const makers = params?.data?.makers || [];
          return makers.map((m: MakerItem) => m.makerName).join(', ');
        },
        cellRenderer: (params: any) => {
          const makers: MakerItem[] = params?.data?.makers || [];
          const count = makers.length;
          if (count === 0) {
            return <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>-</Typography>;
          }
          const firstName = makers[0]?.makerName || '';
          const remainingCount = count - 1;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
              <Typography sx={{ fontSize: 14 }}>{firstName}</Typography>
              {remainingCount > 0 && (
                <Chip
                  label={`+${remainingCount}`}
                  title=""
                  size="small"
                  onClick={() => openMakersDrawer(makers, params?.data?.supplier)}
                  sx={{
                    height: '22px',
                    fontSize: '11px',
                    cursor: 'pointer',
                    backgroundColor: 'primary.main',
                    color: 'white',
                    '&:hover': { backgroundColor: 'primary.dark' },
                  }}
                />
              )}
            </Box>
          );
        },
      },
      {
        field: 'managers',
        headerName: t('table.columns.managers'),
        width: 250,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
        // Return comma-separated manager names for filter
        valueGetter: (params: any) => {
          const managers = params?.data?.managers || [];
          return managers.map((m: ManagerItem) => m.managerName).join(', ');
        },
        cellRenderer: (params: any) => {
          const managers: ManagerItem[] = params?.data?.managers || [];
          const count = managers.length;
          if (count === 0) {
            return <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>-</Typography>;
          }
          const firstName = managers[0]?.managerName || '';
          const remainingCount = count - 1;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
              <Typography sx={{ fontSize: 14 }}>{firstName}</Typography>
              {remainingCount > 0 && (
                <Chip
                  label={`+${remainingCount}`}
                  title=""
                  size="small"
                  onClick={() => openManagersDrawer(managers, params?.data?.supplier)}
                  sx={{
                    height: '22px',
                    fontSize: '11px',
                    cursor: 'pointer',
                    backgroundColor: 'primary.main',
                    color: 'white',
                    '&:hover': { backgroundColor: 'primary.dark' },
                  }}
                />
              )}
            </Box>
          );
        },
      },
      {
        field: 'status',
        headerName: t('table.columns.status'),
        width: 150,
        maxWidth: 150,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
        cellRenderer: (params: any) => <Chips width="100px" label={params?.data?.status} />,
      },
      {
        field: 'actions',
        headerName: '',
        width: 50,
        maxWidth: 50,
        flex: 0,
        sortable: false,
        pinned: 'right',
        cellRenderer: (params: any) => (
          <CustomActionButton
            menuOptions={[
              {
                label: t('buttons.edit'),
                onClick: () => handleEditClick(params.data),
              },
              {
                label: t('buttons.delete'),
                onClick: () => onDeleteClick(params.data),
              },
            ]}
          />
        ),
      },
    ],
    [handleEditClick, openManagersDrawer, openMakersDrawer, onDeleteClick, t],
  );

  useEffect(() => {
    setTableColumns(canAddorEdit ? columns : columns.filter((col) => col.field !== 'actions'));
  }, [canAddorEdit, columns]);

  // Breadcrumb options
  const breadcrumbOptions: BreadcrumbOption[] = [
    { label: t('companies.suppliers'), isActive: true, onClick: () => {} },
  ];

  // If companyId wasn't available at click time, resolve it once companies load
  const companyIdValue = watch('companyId');
  useEffect(() => {
    if (!editingSupplier) return;
    if (companyIdValue) return;
    const candidate = (companiesMakersData?.companies || []).find(
      (c: any) => String(c.label) === String((editingSupplier as any)?.supplier),
    );
    if (candidate) {
      setValue('companyId', String(candidate.value));
    }
  }, [companiesMakersData, editingSupplier, companyIdValue, setValue]);

  // Form submission handler
  const onSubmit = useCallback(
    async (values: SupplierFormValues) => {
      try {
        if (editingSupplier) {
          // UPDATE: Use the companyId from editingSupplier directly
          const updatePayload = {
            companyId: Number((editingSupplier as any)?.id ?? 0),
            shortName: values.shortName,
            active: values.status === 'Active',
            makerIds: values.makers?.map(Number) || [],
            managerIds: values.managers?.map(Number) || [],
          };

          const supplierId = Number((editingSupplier as any)?.id ?? (editingSupplier as any)?.srno);
          const res: any = await updateSupplier({
            id: supplierId,
            payload: updatePayload,
          }).unwrap();
          const message = res?.message;
          showSuccessToast(message);
          refetch(); // Refetch suppliers list to update the table
        } else {
          // CREATE: Map company name to company ID from dropdown
          const companyIdNumber =
            companiesSuppliersData?.companies?.find(
              (c: any) =>
                String(c.label) === String(values.companyId) ||
                String(c.value) === String(values.companyId),
            )?.value ?? null;

          const createPayload = {
            companyId: companyIdNumber ? Number(companyIdNumber) : 0,
            shortName: values.shortName,
            active: values.status === 'Active',
            makerIds: values.makers?.map(Number) || [],
            managerIds: values.managers?.map(Number) || [],
          };

          const res: any = await createSupplier(createPayload).unwrap();
          const message = res?.message;
          showSuccessToast(message);
          refetch();
        }

        // Only close modal and reset form on SUCCESS
        setShowAddCompaniesSuppliersModal(false);
        setEditingSupplier(null);
        setDidApplyPrefill(false);
        reset({
          shortName: '',
          companyId: '',
          makers: [],
          managers: [],
          status: 'Active',
        });
      } catch (error: any) {
        logger.error('Failed to create supplier', error as Error);
      }
    },
    [editingSupplier, companiesSuppliersData, createSupplier, updateSupplier, refetch, reset],
  );

  // Modal close handler
  const handleCloseModal = useCallback(() => {
    setShowAddCompaniesSuppliersModal(false);
    setEditingSupplier(null);
    setDidApplyPrefill(false);
    reset({
      shortName: '',
      companyId: '',
      makers: [],
      managers: [],
      status: 'Active',
    });
  }, [reset]);

  return (
    <Stack gap={1} sx={{ height: '100%', minHeight: 0 }}>
      <PageHeader
        actions={actionBtns}
        options={breadcrumbOptions}
        isFilterActive={isFilterActive}
        resetFilters={() => setResetFiltersSignal(true)}
      />
      <TableCardContent>
        <ClientSideAgGrid
          rowData={companiesSuppliersList ?? []}
          columnDefs={tableColumns as any}
          loading={isLoading}
          className="companies-suppliers-grid"
          onFiltersActiveChange={(active: boolean) => {
            setIsFilterActive(active);
            setResetFiltersSignal(false);
          }}
          resetFiltersSignal={resetFiltersSignal}
        />
      </TableCardContent>

      {showDeleteConfirm && (
        <DeleteConfirmModal
          open={showDeleteConfirm}
          title={t('companies.deleteSupplier')}
          description={t('common.deleteConfirmation')}
          isSubmitting={isDeleting}
          onClose={onCloseDeleteConfirm}
          onConfirm={handleConfirmDelete}
        />
      )}

      <SideDrawer
        open={isMakersDrawerOpen}
        onClose={closeMakersDrawer}
        title={
          isMakersDrawerOpen
            ? `${t('table.columns.supplier')}: ${selectedSupplierName || ''}`
            : t('table.columns.supplier')
        }
        size="xs"
      >
        <Box display="flex" flexDirection="column" sx={{ p: 3 }}>
          {selectedMakers.map((m: MakerItem, index: number) => (
            <Box key={m.makerId}>
              <Typography
                sx={{
                  fontSize: 14,
                  color: theme.palette.sideDrawer.title,
                  fontWeight: 500,
                  py: 1,
                }}
              >
                {index + 1}. {m.makerName}
              </Typography>
              {index < selectedMakers.length - 1 && (
                <Divider sx={{ my: 1, borderColor: theme.palette.divider }} />
              )}
            </Box>
          ))}
          {selectedMakers.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography
                sx={{
                  fontSize: 14,
                  color: theme.palette.text.secondary,
                }}
              >
                {t('companies.noMakersFound')}
              </Typography>
            </Box>
          )}
        </Box>
      </SideDrawer>

      <SideDrawer
        open={isManagersDrawerOpen}
        onClose={closeManagersDrawer}
        title={
          isManagersDrawerOpen
            ? `${t('table.columns.supplier')}: ${selectedManagerSupplierName || ''}`
            : t('table.columns.supplier')
        }
        size="xs"
      >
        <Box display="flex" flexDirection="column" sx={{ p: 3 }}>
          {selectedManagers.map((m: ManagerItem, index: number) => (
            <Box key={m.managerId}>
              <Typography
                sx={{
                  fontSize: 14,
                  color: theme.palette.sideDrawer.title,
                  fontWeight: 500,
                  py: 1,
                }}
              >
                {index + 1}. {m.managerName}
              </Typography>
              {index < selectedManagers.length - 1 && (
                <Divider sx={{ my: 1, borderColor: theme.palette.divider }} />
              )}
            </Box>
          ))}
          {selectedManagers.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography
                sx={{
                  fontSize: 14,
                  color: theme.palette.text.secondary,
                }}
              >
                {t('companies.noManagersFound')}
              </Typography>
            </Box>
          )}
        </Box>
      </SideDrawer>

      {showAddCompaniesSuppliersModal && (
        <EditCompaniesSuppliersModal
          data={{
            companiesSuppliers: companiesSuppliersData?.companiesSuppliers ?? [],
            companies: companiesSuppliersData?.companies ?? [],
            makers:
              (makersList as any[])?.map((m: any) => ({
                value: Number(m.id),
                label: String(m.shortName),
              })) || [],
            managers: (managersShortList || []) as any,
            statusOptions: companiesSuppliersData?.statusOptions ?? [
              { value: 'Active', label: t('common.active') },
              { value: 'Inactive', label: t('common.inactive') },
            ],
          }}
          onClose={handleCloseModal}
          onSubmit={handleSubmit(onSubmit)}
          control={control}
          errors={errors}
          isEditMode={!!editingSupplier}
          isSubmitting={false}
        />
      )}
    </Stack>
  );
};

export default CompaniesSuppliersContainer;
